/**
 * One formatter for a water-rate BAND, shared by every surface that prints one.
 *
 * Two independent copies of this logic existed and disagreed. The copy in
 * `callReadyFindings.ts` chose the unit from the band (`range.high`); the copy
 * in `sedEconomicEvidence.ts` chose it per value. Per-value is wrong for any
 * range that straddles 1,000,000 gal/day, because the two ends then print in
 * different units:
 *
 *     201,487 gal/day-1.83 MGD        <- what the report actually said
 *     0.20–1.83 MGD                   <- the same band, one unit
 *
 * Pineywoods Park's modern operator-fleet row is exactly that case. A reader
 * comparing it against the wet-tower rows (3.07–14.74 MGD) has to notice the
 * unit switch mid-span to avoid reading a ~9x error.
 *
 * The rules are:
 *   - the BAND picks the unit, and both ends use it. The large unit (MGD,
 *     million gal/year) needs the band's high end at or above one unit AND its
 *     low end at or above a tenth of one, so the low end keeps two significant
 *     figures at 2 decimals; otherwise the band prints in whole gallons. A small
 *     site's `0.07–2.00 million gal/year` kept one significant figure at the low
 *     end, and a low end under 5,000 gal/day would have printed `0.00 MGD` —
 *     a nonzero quantity shown as zero;
 *   - the unit is printed ONCE, after the high end (`0.20–1.83 MGD`). Printing
 *     it on both ends (`0.20 MGD–1.83 MGD`) made the WATER-TRUTH-1 guard
 *     (`waterUseScreeningGuard.ts`) read each end as a separate single value,
 *     which it matches against EITHER end of ANY band — so a restatement that
 *     paired one scenario's low with another scenario's high passed. With the
 *     unit once, the guard parses the span as one pair and checks both ends
 *     against the SAME band;
 *   - when both ends DISPLAY identically the band collapses to one value;
 *   - MGD and million gal/year print 2 decimals; gal/day, gal/year and
 *     gal/hour print whole gallons with thousands separators. These are the
 *     forms the guard's `endpointMatches` accepts for an exact endpoint.
 */

export interface WaterRateBand { low: number; high: number }

const MGD_THRESHOLD_GALLONS_PER_DAY = 1_000_000;
const GALLONS_PER_MILLION = 1_000_000;
/** The smallest low end, as a fraction of the large unit, that still shows two significant figures at 2 dp. */
const LARGE_UNIT_MIN_LOW_FRACTION = 0.1;

const TWO_DECIMALS = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const WHOLE = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

/** `low–high unit`, or `value unit` when both ends display the same. */
function bandWithUnitOnce(range: WaterRateBand, formatValue: (value: number) => string, unit: string): string {
  const low = formatValue(range.low);
  const high = formatValue(range.high);
  return low === high ? `${low} ${unit}` : `${low}–${high} ${unit}`;
}

/** True when both ends of the band read meaningfully in the large unit. */
function bandUsesLargeUnit(range: WaterRateBand, unitGallons: number): boolean {
  return Math.max(range.low, range.high) >= unitGallons
    && Math.min(range.low, range.high) >= unitGallons * LARGE_UNIT_MIN_LOW_FRACTION;
}

/** Gallons/day band, rendered in a single unit chosen from the band. */
export function formatWaterRateBand(range: WaterRateBand): string {
  return bandUsesLargeUnit(range, MGD_THRESHOLD_GALLONS_PER_DAY)
    ? bandWithUnitOnce(range, value => TWO_DECIMALS.format(value / MGD_THRESHOLD_GALLONS_PER_DAY), 'MGD')
    : bandWithUnitOnce(range, value => WHOLE.format(value), 'gal/day');
}

/** Gallons/year band, same band-picks-the-unit rule. */
export function formatAnnualWaterBand(range: WaterRateBand): string {
  return bandUsesLargeUnit(range, GALLONS_PER_MILLION)
    ? bandWithUnitOnce(range, value => TWO_DECIMALS.format(value / GALLONS_PER_MILLION), 'million gal/year')
    : bandWithUnitOnce(range, value => WHOLE.format(value), 'gal/year');
}

/** Gallons/hour band (peak active-hour screens): whole gallons, unit once. */
export function formatHourlyWaterBand(range: WaterRateBand): string {
  return bandWithUnitOnce(range, value => WHOLE.format(value), 'gal/hour');
}
